import React, { useCallback, useEffect, useState } from "react";
import { Box, Flex, useToast } from "@chakra-ui/react";
import i18n from "../../../../../i18n/i18n";
import { appStore } from "../../../../../zustand/globalStore";
import ModalHeaderComponent from "../../../../../components/Common/ModalHeaderComponent";
import {
  createMatch,
  getMatchById,
  updateMatch,
} from "../../../../../models/MatchModel";
import { stepsTournaments } from "../../../../../definitions/constants/GlobalConstants";
import SpinnerComponent from "../../../../../components/Common/SpinnerComponent";
import CustomTextInput from "../../../../../components/Common/CustomTextInput";
import CitySelector from "../../../../../components/Common/CitySelector";
import { PlaceDetails } from "../../../../../definitions/interfaces/Place.interface";
import CustomMenuSelect from "../../../../../components/Shared/CustomMenuSelect";
import SelectDateAndTime from "../../../../../components/Common/SelectDateAndTime";

interface CreateMatchBaseProps {
  isEdit: boolean;
  currentStep: string;
  goToStep: (key: string) => void;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  teams: any;
  selectedPhase: { [key: string]: any };
  selectedGroup: { [key: string]: any };
}

type CreateOrEditMatchProps =
  | (CreateMatchBaseProps & {
      isEdit: false;
      selectedMatch?: never;
    })
  | (CreateMatchBaseProps & {
      isEdit: true;
      selectedMatch: { [key: string]: any };
    });

const CreateOrEditMatch: React.FC<CreateOrEditMatchProps> = ({
  isEdit,
  goToStep,
  currentStep,
  isSaving,
  setIsSaving,
  selectedMatch,
  selectedPhase,
  selectedGroup,
  teams,
}) => {
  const toast = useToast();
  const [matchData, setMatchData] = useState<any>();
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [place, setPlace] = useState<PlaceDetails | null>(null);
  const [firstTeam, setFirstTeam] = useState<any>(null);
  const [secondTeam, setSecondTime] = useState<any>(null);
  const [matchDate, setMatchDate] = useState<any>(new Date());
  const [referees, setReferees] = useState<any>();
  const [vocal, setVocal] = useState<any>();
  const [formData, setFormData] = useState<{ field_name: string }>({
    field_name: "",
  });
  const { userData, currentTournament, hideContainerModal } = appStore(
    (state) => state
  );

  console.log("selectedMatch", selectedMatch);
  console.log("matchData", matchData);

  const fetchCurrentMatch = useCallback(async () => {
    try {
      const matchData = await getMatchById(selectedMatch.id);
      setMatchData(matchData);
    } catch (error) {
      if (error instanceof Error) {
        console.error("Error fetching current match:", error.message);
      } else {
        console.error("Unexpected error:", error);
      }
    }
  }, [selectedMatch?.id]);

  useEffect(() => {
    if (isEdit && selectedMatch) {
      fetchCurrentMatch();
    }
  }, [fetchCurrentMatch, isEdit, selectedMatch]);

  useEffect(() => {
    if (isEdit && matchData) {
      setFormData({
        field_name: matchData.field_name || "",
      });
      setPlace(matchData.place || null);
      setFirstTeam(matchData.first_team.name || null);
      setSecondTime(matchData.second_team.name || null);
      setMatchDate(matchData.match_date || new Date());
    }
  }, [matchData, isEdit]);

  const onPressSaveMatch = async (data: any) => {
    if (isSaving) return;
    setIsSaving(true);
    let dataToSave: any = {};
    dataToSave.first_team_id = firstTeam.id;
    dataToSave.second_team_id = secondTeam.id;
    if (dataToSave.first_team_id === dataToSave.second_team_id) {
      toast({
        title: i18n.t("selectDifferentTeams"),
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    if (matchDate) dataToSave.match_date = matchDate;
    if (place) {
      dataToSave.place_description = place.description;
      dataToSave.place = place;
    }
    if (data.field_name) dataToSave.field_name = data.field_name;
    if (referees) dataToSave.referees = referees;
    if (vocal) dataToSave.vocal_user_id = vocal.id;
    if (selectedMatch.id) {
      await updateMatch(
        selectedMatch?.id,
        dataToSave,
        selectedMatch?.tournament_id,
        selectedMatch?.phase_id,
        selectedMatch?.group_id
      );
    } else {
      await createMatch(
        userData.id,
        dataToSave,
        currentTournament.id,
        selectedPhase,
        selectedGroup.id
      );
    }

    setIsSaving(false);
  };

  const validateFields = () => {
    const newErrors: { [key: string]: string } = {};
    const fieldsToValidate = [
      {
        key: "field_name",
        value: formData.field_name,
      },
      { key: "city", value: place },
      {
        key: "firstTeam",
        value: firstTeam,
      },
      {
        key: "secondTeam",
        value: secondTeam,
      },
    ];

    fieldsToValidate.forEach((field) => {
      if (!field.value) newErrors[field.key] = i18n.t("fieldRequired");
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateFields()) {
      onPressSaveMatch(formData);
    } else {
      return;
    }
  };

  return (
    <>
      {isSaving ? (
        <SpinnerComponent spinnerKey="modal" />
      ) : (
        <Box
          as="form"
          onSubmit={handleSubmit}
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap={4}
        >
          {(isEdit
            ? currentStep === stepsTournaments.editMatch
            : currentStep === stepsTournaments.newMatch) && (
            <Flex gap={4} flexDirection="column">
              <ModalHeaderComponent
                title={isEdit ? i18n.t("editMatch") : i18n.t("newMatch")}
                subtitle={i18n.t("readyTournament")}
                confirmText={i18n.t("continue")}
                onCancel={hideContainerModal}
                onBack={() => goToStep(stepsTournaments.matchesOf)}
                onConfirm={() =>
                  isEdit
                    ? goToStep(stepsTournaments.editTechnicalTeam)
                    : goToStep(stepsTournaments.technicalTeam)
                }
                isSaving={isSaving}
                hasMoreSteps={true}
                actionButtonsRequired={true}
              />
              <Flex gap={2} flexDirection="column">
                <CustomMenuSelect
                  options={teams}
                  isMatch={true}
                  labelName={i18n.t("teamFirst")}
                  error={errors.firstTeam}
                  isInvalid={!!errors.firstTeam}
                  selectedDescription={firstTeam}
                  setSelectedDescription={setFirstTeam}
                />
                <CustomMenuSelect
                  options={teams}
                  isMatch={true}
                  labelName={i18n.t("teamSecond")}
                  error={errors.secondTeam}
                  isInvalid={!!errors.secondTeam}
                  selectedDescription={secondTeam}
                  setSelectedDescription={setSecondTime}
                />
                <SelectDateAndTime
                  label={i18n.t("DateAndTime")}
                  date={matchDate}
                  isRegulation={false}
                  setDate={(date: any) => setMatchDate(date)}
                  placeholderDate={i18n.t("selectDate")}
                  placeholderTime={i18n.t("selectHour")}
                />
                <CustomTextInput
                  textField={i18n.t("sportsScenario")}
                  placeholder={i18n.t("sportsScenario") + "..."}
                  defaultValue={formData.field_name}
                  onChangeValue={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      field_name: e.target.value,
                    }));
                  }}
                  isPassword={false}
                  isRequired={false}
                  typeInput="text"
                />
                <CitySelector
                  label={i18n.t("address")}
                  placeholder={i18n.t("selectAddress") + "..."}
                  error={errors.city}
                  isInvalid={!!errors.city}
                  place={place}
                  setPlace={setPlace}
                />
              </Flex>
            </Flex>
          )}
          {(isEdit
            ? currentStep === stepsTournaments.editTechnicalTeam
            : currentStep === stepsTournaments.technicalTeam) && (
            <Flex gap={4} flexDirection="column">
              <ModalHeaderComponent
                title={i18n.t("technicalStaffTeam")}
                subtitle={i18n.t("readyTournament")}
                confirmText={i18n.t("save")}
                onCancel={hideContainerModal}
                onBack={() =>
                  isEdit
                    ? goToStep(stepsTournaments.editMatch)
                    : goToStep(stepsTournaments.newMatch)
                }
                onConfirm={() =>
                  isEdit
                    ? goToStep(stepsTournaments.successEditMatch)
                    : goToStep(stepsTournaments.successNewMatch)
                }
                isSaving={isSaving}
                hasMoreSteps={true}
                actionButtonsRequired={true}
              />
              <Flex gap={2} flexDirection="column">
                <Flex gap={8} justifyContent="space-between">
                  <CustomMenuSelect
                    options={teams}
                    labelName={i18n.t("referee")}
                    error={errors.firstTeam}
                    isInvalid={!!errors.firstTeam}
                    selectedDescription={firstTeam}
                    setSelectedDescription={setFirstTeam}
                  />
                  <CustomMenuSelect
                    options={teams}
                    labelName={i18n.t("assistantReferee5")}
                    error={errors.secondTeam}
                    isInvalid={!!errors.secondTeam}
                    selectedDescription={secondTeam}
                    setSelectedDescription={setSecondTime}
                  />
                </Flex>
                <Flex gap={8} justifyContent="space-between">
                  <CustomMenuSelect
                    options={teams}
                    labelName={i18n.t("assistantReferee1")}
                    error={errors.firstTeam}
                    isInvalid={!!errors.firstTeam}
                    selectedDescription={firstTeam}
                    setSelectedDescription={setFirstTeam}
                  />
                  <CustomMenuSelect
                    options={teams}
                    labelName={i18n.t("assistantReferee6")}
                    error={errors.secondTeam}
                    isInvalid={!!errors.secondTeam}
                    selectedDescription={secondTeam}
                    setSelectedDescription={setSecondTime}
                  />
                </Flex>
                <Flex gap={8} justifyContent="space-between">
                  <CustomMenuSelect
                    options={teams}
                    labelName={i18n.t("assistantReferee2")}
                    error={errors.firstTeam}
                    isInvalid={!!errors.firstTeam}
                    selectedDescription={firstTeam}
                    setSelectedDescription={setFirstTeam}
                  />
                  <CustomMenuSelect
                    options={teams}
                    labelName={i18n.t("vocal")}
                    error={errors.secondTeam}
                    isInvalid={!!errors.secondTeam}
                    selectedDescription={secondTeam}
                    setSelectedDescription={setSecondTime}
                  />
                </Flex>
                <Flex gap={8} justifyContent="space-between">
                  <CustomMenuSelect
                    options={teams}
                    labelName={i18n.t("fourthOfficial")}
                    error={errors.firstTeam}
                    isInvalid={!!errors.firstTeam}
                    selectedDescription={firstTeam}
                    setSelectedDescription={setFirstTeam}
                  />
                </Flex>
              </Flex>
            </Flex>
          )}
        </Box>
      )}
    </>
  );
};

export default CreateOrEditMatch;
